import { type ASTExpr } from '../ast';
import { Opaque } from '../tmpCopied/Opaque';

/*
 * Definition of a conversion between two units.  The specific units are
 * not specified here, but are instead specified in the registry.
 */
export interface Conversion {
  fromBase: (input: ASTExpr) => ASTExpr; // Conversion to the unit from the base unit
  toBase: (input: ASTExpr) => ASTExpr; // Conversion from the unit to the base unit
}

/*
 * An atomic unit is an opaque string identifier for a single unit dimension,
 * e.g. "meter", "kilogram", "second".
 */
export type AtomicUnit = Opaque<string, 'AtomicUnit'>;

/*
 * A unit is a collection of atomic units mapped to exponents, plus optional
 * scale factors. For example, the unit "meter per second" is represented as
 * { meter: 1, second: -1 }.
 *
 * This class wraps a Map internally but is not a Map itself, to prevent
 * accidental lossy copies via `new Map(unit)` and to cleanly support
 * additional properties like scale factors.
 *
 * Scale factors allow representing units like "thousands of kilogram"
 * (numeratorScaleFactor = 1000) or "meter per thousands of kilogram"
 * (denominatorScaleFactor = 1000). Keeping separate numerator/denominator
 * scale factors avoids floating-point precision loss and makes
 * unitToString unambiguous about where to attach each factor.
 *
 * Implements Iterable so that existing `for (const [atomicUnit, exp] of unit)`
 * loops continue to work without changes.
 */
export class Unit implements Iterable<[AtomicUnit, number]> {
  /** The backing store mapping each atomic unit to its exponent. */
  private readonly _exponents: Map<AtomicUnit, number>;

  /** Scale factor applied to the numerator (e.g. 1000 for "1000*kilogram"). */
  numeratorScaleFactor: number | undefined;

  /** Scale factor applied to the denominator (e.g. 1000 for "meter/1000*kilogram"). */
  denominatorScaleFactor: number | undefined;

  constructor(entries?: ReadonlyArray<[AtomicUnit, number]>) {
    this._exponents = new Map(entries);
  }

  /**
   * Create a Unit by copying from an existing Unit, Map, or array of entries.
   * This is the class equivalent of `new Map(otherMap)`.
   */
  static from(
    source:
      | Iterable<[AtomicUnit, number]>
      | ReadonlyArray<[AtomicUnit, number]>
      | Unit
  ): Unit {
    if (source instanceof Unit) {
      const copy = new Unit([...source._exponents]);
      copy.numeratorScaleFactor = source.numeratorScaleFactor;
      copy.denominatorScaleFactor = source.denominatorScaleFactor;
      return copy;
    }
    return new Unit([...source]);
  }

  get size(): number {
    return this._exponents.size;
  }

  get(key: AtomicUnit): number | undefined {
    return this._exponents.get(key);
  }

  set(key: AtomicUnit, value: number): this {
    this._exponents.set(key, value);
    return this;
  }

  has(key: AtomicUnit): boolean {
    return this._exponents.has(key);
  }

  delete(key: AtomicUnit): boolean {
    return this._exponents.delete(key);
  }

  forEach(
    callbackfn: (value: number, key: AtomicUnit, unit: Unit) => void
  ): void {
    this._exponents.forEach((value, key) => {
      callbackfn(value, key, this);
    });
  }

  keys(): MapIterator<AtomicUnit> {
    return this._exponents.keys();
  }

  values(): MapIterator<number> {
    return this._exponents.values();
  }

  entries(): MapIterator<[AtomicUnit, number]> {
    return this._exponents.entries();
  }

  /** Makes `for (const [unit, exp] of myUnit)` work. */
  [Symbol.iterator](): Iterator<[AtomicUnit, number]> {
    return this._exponents[Symbol.iterator]();
  }
}

/*
 * A convenience type for specifying units.  This can be either a unit string,
 * or an object with the same atomic unit/exponent pairs that will go into the map.
 */
export type UnitSpec = Record<string, number> | string;

/**
 * A non-exhaustive list of unit groups. Each group represents a set of units
 * that are commensurable with one another. Not every unit is represented here,
 * as it's possible to write arbitrarily complex compound units.
 *
 * These groups exist because:
 *
 * 1. BI unit conversion uses them to determine which units within a field are
 *    commensurable (see convertUnitMiddleware.ts).
 * 2. Reference data for Supply Chain LCAs is organized by unit group (see
 *    SupplierEmissionsFactorsService.ts).
 *
 * @deprecated Unless needed for one of the above cases, do not add new unit
 * groups or rely on these groups in new code. Instead, check commensurability
 * directly using `isUnitCommensurable`.
 */
export const UNIT_GROUPS = [
  'area',
  'length',
  'mass',
  'volume',
  'time',
  'calendar',
  'fuelEconomy',
  'energyEconomy',
  'temperature',
  'textile',
  'energy',
  'power',
  'count',
  'currency',
  'information',
  'force',
  'carbon',
  'storage',
  'transport',
] as const;

/**
 * A group of units that are commensurable with one another. Not every unit
 * is represented by one of these named unit groups.
 *
 * @deprecated See UNIT_GROUPS for more information.
 */
export type UnitGroup = (typeof UNIT_GROUPS)[number];
